import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, getEChartsTooltipTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseHistoricalTreasuryRates {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private selectedMaturities: Set<string> = new Set(['1M', '3M', '6M', '1Y', '2Y', '5Y', '10Y', '30Y']);
    private hiddenSeries: Set<string> = new Set();
    private selectedTimePeriod: string = '1M';
    private customStartDate: string | null = null;
    private customEndDate: string | null = null;
    private widgetResizeObserver: ResizeObserver | null = null;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        this.attachStyles();
        this.setupEventListeners();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
  <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Historical Treasury Rates</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow" data-testing-id="options-row">
                <div class="item" data-testing-id="period-selector">
                    <select id="timeperiods" class="period-select" data-testing-id="period-select">
                        <option value="1M">1 Month</option>
                        <option value="3M">3 Months</option>
                        <option value="6M">6 Months</option>
                        <option value="1Y">1 Year</option>
                        <option value="5Y">5 Years</option>
                    </select>
                </div>
                <span></span>
                <div class="item" data-testing-id="legends-container">
                    <div class="legendsCollection" data-testing-id="legends-collection">
                        <span class="toggle-series active" data-series-index="0" data-maturity="1M" data-tooltip="1 Month" style="background-color:#10B981;" data-testing-id="legend-1m"></span>
                        <span class="toggle-series active" data-series-index="1" data-maturity="3M" data-tooltip="3 Month" style="background-color:#7C3AED;" data-testing-id="legend-3m"></span>
                        <span class="toggle-series active" data-series-index="2" data-maturity="6M" data-tooltip="6 Month" style="background-color:#60A5FA;" data-testing-id="legend-6m"></span>
                        <span class="toggle-series active" data-series-index="3" data-maturity="1Y" data-tooltip="1 Year" style="background-color:#FB8C00;" data-testing-id="legend-1y"></span>
                        <span class="toggle-series active" data-series-index="4" data-maturity="2Y" data-tooltip="2 Year" style="background-color:#F97316;" data-testing-id="legend-2y"></span>
                        <span class="toggle-series active" data-series-index="5" data-maturity="5Y" data-tooltip="5 Year" style="background-color:#34D399;" data-testing-id="legend-5y"></span>
                        <span class="toggle-series active" data-series-index="6" data-maturity="10Y" data-tooltip="10 Year" style="background-color:#60A5FA;" data-testing-id="legend-10y"></span>
                        <span class="toggle-series active" data-series-index="7" data-maturity="30Y" data-tooltip="30 Year" style="background-color:#A78BFA;" data-testing-id="legend-30y"></span>
                    </div>
                </div>
            </div>

            <div class="chartDv" data-testing-id="chart-container">
                <div class="chart" id="marketRealtimeChart" data-testing-id="realtime-chart"></div>
            </div>

          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
                placeholder.replaceWith(dropdown);
            }
        }
    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        dropdown?.addEventListener('change', e => {
            const value = (e.target as HTMLSelectElement).value;
            this.changeSymbol(value);
        });
        if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
        
        const legendSpans = Array.from(this.shadowRoot.querySelectorAll('.toggle-series')) as HTMLElement[];
        legendSpans.forEach(span => {
            span.addEventListener('click', (e) => {
                const target = e.currentTarget as HTMLElement;
                const maturity = target.getAttribute('data-maturity') || '';
                if (!maturity) return;
                const isActiveNow = target.classList.toggle('active');

                if (isActiveNow) {
                    this.selectedMaturities.add(maturity);
                    this.hiddenSeries.delete(maturity);
                } else {
                    this.selectedMaturities.delete(maturity);
                    this.hiddenSeries.add(maturity);
                }

                this.setupChart(this.config.symbol!);
            });
        });

        const periodSelect = this.shadowRoot.getElementById('timeperiods') as HTMLSelectElement | null;
        if (periodSelect) {
            periodSelect.value = this.selectedTimePeriod;
            periodSelect.addEventListener('change', () => {
                const period = periodSelect.value;
                this.selectedTimePeriod = period;
                this.customStartDate = null;
                this.customEndDate = null;
                this.setupChart(this.config.symbol!);
            });
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(symbol?: string, stockInfo?: any): Promise<void> {
        try {
            if (this.selectedMaturities.size === 0) {
                this.selectedMaturities.add('5Y');
            }
            const maturitiesParams = Array.from(this.selectedMaturities).map(m => `maturities=${encodeURIComponent(m)}`).join('&');
            let datePart = '';
            if (this.customStartDate && this.customEndDate) {
                datePart = `start_date=${encodeURIComponent(this.customStartDate)}&end_date=${encodeURIComponent(this.customEndDate)}`;
            } else {
                datePart = `timePeriod=${encodeURIComponent(this.selectedTimePeriod)}`;
            }

            const url = `${APIURL}/v1/discover/historical-treasury-rates?${maturitiesParams}&${datePart}`;

            const response = await apiGet({
                method: 'GET',
                url,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) throw new Error("Failed to fetch data");
            this.currentData = await response.json();

            this.updateDisplay();
            this.emit("dataUpdate", this.currentData);

        } catch (err) {
            console.error("Error fetching chart data:", err);
            this.currentData = null;
            this.updateDisplay();
        }
    }

    private updateDisplay(): void {
        const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
        const container = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;

        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }

        const hasData =
            this.currentData?.data &&
            Array.isArray(this.currentData.data) &&
            this.currentData.data.length > 0;

        if (!hasData) {
            chartDv?.removeAttribute('data-testing-id');
            if (this.currentChart) {
                try { this.currentChart.dispose(); } catch { /* ignore */ }
                this.currentChart = null;
            }
            if (container) container.innerHTML = getNoDataHTML();
            return;
        }

        this.renderAnalystRating();
    }

    private renderAnalystRating(): void {
        this.drawChart();
    }
    
    formatDateToMMMDD(dateString: string): string {
        const date = new Date(dateString);
        const options = { month: 'short', day: 'numeric' } as const;
        return date.toLocaleDateString('en-US', options);
    }
    
    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;
        const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;

        if (!container || !this.currentData?.data) return;
        const hasData =
        this.currentData &&
        this.currentData.data &&
        Array.isArray(this.currentData.data) &&
        this.currentData.data.length > 0;

    if (!hasData) {
        chartDv?.removeAttribute('data-testing-id');
        container.innerHTML = getNoDataHTML();
        return;
    }
        chartDv?.setAttribute('data-testing-id', 'historical-treasury-chartdv');


        const data = this.currentData.data;
        const arr = Array.isArray(data) ? data : [];
        const dates = arr.map(item => this.formatDateToMMMDD(item.date));
        const selected = Array.from(this.selectedMaturities);
        const series: any[] = [];
        const legendSpansForColors = Array.from(this.shadowRoot.querySelectorAll('.toggle-series')) as HTMLElement[];
        const colorMap: Record<string, string> = {};
        
        legendSpansForColors.forEach((span, idx) => {
            const m = span.getAttribute('data-maturity') || `Series${idx + 1}`;
            let bg = span.style.backgroundColor;
            try {
                if (!bg) bg = window.getComputedStyle(span).backgroundColor || '';
            } catch (e) { bg = bg || ''; }
            colorMap[m] = bg || '';
        });
        
        const findKeyForMaturity = (maturity: string): string | null => {
            const num = (maturity.match(/\d+/) || [''])[0];
            const isYear = /Y$/i.test(maturity);
            const isMonth = /M$/i.test(maturity);

            const candidates = [maturity, maturity.toLowerCase(), maturity.replace(/\s+/g, '')];
            if (num) {
                if (isYear) {
                    candidates.push('year' + num, 'y' + num, num + 'Y', num + 'y');
                }
                if (isMonth) {
                    candidates.push('month' + num, 'm' + num, num + 'M', num + 'm');
                }
            }

            const uniq = Array.from(new Set(candidates));
            for (const c of uniq) {
                for (const it of arr) {
                    if (!it) continue;
                    if (Object.prototype.hasOwnProperty.call(it, c) && it[c] !== undefined) return c;
                }
            }

            const normalizedTarget = maturity.replace(/\W/g, '').toLowerCase();
            for (const it of arr) {
                if (!it) continue;
                for (const k of Object.keys(it)) {
                    if (String(k).replace(/\W/g, '').toLowerCase() === normalizedTarget) return k;
                }
            }

            return null;
        };

        for (const mat of selected) {
            const key = findKeyForMaturity(mat);
            if (!key) continue;
            const seriesData = arr.map(item => {
                if (!item) return null;
                const v = (item as any)[key];
                return v === undefined ? null : v;
            });
            series.push({ name: mat, type: 'line', smooth: true, data: seriesData, showSymbol: false, lineStyle: { width: 2 }, itemStyle: {} });
        }

        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
            this.currentChart = null;
        }

        const chart = echarts.init(container);
        this.currentChart = chart;

        // const isBlack = this.config.theme === 'black';
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');

        const palette = ['#10B981', '#7C3AED', '#60A5FA', '#FB8C00', '#F97316', '#34D399', '#60A5FA', '#A78BFA'];

        const option: any = {
            backgroundColor: 'transparent',
            color: palette,
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'line' },
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    if (!params || params.length === 0) return '';
                    const axisName = params[0].axisValueLabel || '';
                    const lines = params.map((p: any) => `${p.marker} ${p.seriesName}: ${p.data === null || p.data === undefined ? '-' : p.data}%`);
                    return `<div>${axisName}</div><div>${lines.join('<br/>')}</div>`;
                }
            },
            legend: { show: false },
            grid: {
                top: '10',
                left: '0',
                right: '20',
                bottom: '0',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: dates,
                boundaryGap: false,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { color: textColor, fontSize: 12 }
            },
            yAxis: {
                type: 'value',
                scale: true,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { color: textColor, formatter: (v: any) => (v === null || v === undefined ? '' : `${v}%`) },
                splitLine: { show: true, lineStyle: { color: splitLineColor } }
            },
            series: series.map((s: any, i: number) => {
                const maturityName = s && s.name ? String(s.name) : String(i);
                const colorFromLegend = colorMap[maturityName];
                const color = (colorFromLegend && colorFromLegend.length > 0) ? colorFromLegend : palette[i % palette.length];
                return {
                    ...s,
                    lineStyle: { width: 2, color },
                    itemStyle: { color },
                    showSymbol: false
                };
            })
        };

        chart.setOption(option);
        
        // Add data-testing-id to chart after render
        setTimeout(() => {
            const chartSvg = this.shadowRoot.querySelector('#marketRealtimeChart svg');
            if (chartSvg) {
                chartSvg.setAttribute('data-testing-id', 'treasury-chart-svg');
            }
            const legendItems = this.shadowRoot.querySelectorAll('.legend-item');
            legendItems.forEach((item, idx) => {
                item.setAttribute('data-testing-id', `legend-item-${idx}`);
            });
        }, 500);

        try {
            const legendSpans = Array.from(this.shadowRoot.querySelectorAll('.toggle-series')) as HTMLElement[];
            legendSpans.forEach((span, idx) => {
                const maturity = span.getAttribute('data-maturity') || '';
                if (this.selectedMaturities.has(maturity) && !span.classList.contains('active')) span.classList.add('active');
                if (!this.selectedMaturities.has(maturity) && span.classList.contains('active')) span.classList.remove('active');
            });
            this.updateLegendTooltips();
        } catch (e) { }
    }

    private updateLegendTooltips(): void {
        const legendSpans = Array.from(this.shadowRoot.querySelectorAll('.toggle-series')) as HTMLElement[];
        const option = this.currentChart && this.currentChart.getOption ? this.currentChart.getOption() : null;
        const seriesNames: string[] = [];

        if (option && option.series) {
            for (let i = 0; i < option.series.length; i++) {
                const s = option.series[i];
                seriesNames.push(s && s.name ? String(s.name) : `Series ${i + 1}`);
            }
        } else {
            legendSpans.forEach((sp, idx) => {
                const m = sp.getAttribute('data-maturity') || `Series ${idx + 1}`;
                seriesNames.push(m);
            });
        }

        legendSpans.forEach((span, idx) => {
            const name = seriesNames[idx] || span.getAttribute('data-maturity') || `Series ${idx + 1}`;
            try {
                span.setAttribute('data-tooltip', name);
                span.setAttribute('aria-label', name);
                span.title = name;
            } catch (e) { }
        });
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any, stockInfo: any) => this.setupChart(stockId, stockInfo), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseHistoricalTreasuryRates: typeof TraderverseHistoricalTreasuryRates; } }
if (typeof window !== 'undefined') { window.TraderverseHistoricalTreasuryRates = TraderverseHistoricalTreasuryRates; }